import React, { useState, useEffect } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Switch } from '../ui/Switch';
import { Badge } from '../ui/Badge';
import { apiRequest } from '../../services/apiClient';
import toast from 'react-hot-toast';

/**
 * MasterFeaturesModal Component
 * Modal for managing master features for a tenant
 * 
 * @param {Boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Object} tenant - Tenant object with tenantId
 */
const MasterFeaturesModal = ({ isOpen, onClose, tenant }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && tenant?.tenantId) {
      fetchFeatures();
    } else {
      setFeatures([]);
    }
  }, [isOpen, tenant]);

  const fetchFeatures = async () => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID is required');
      return;
    }

    setLoading(true);
    try {
      const orgId = tenant.tenantId;

      // Step 1: Fetch all available master features (all features, not just tenant-specific)
      const featuresResponse = await apiRequest(
        `/gateway/api/ticket/master-features/all`,
        { method: 'GET' }
      );

      //console.log('📦 All features response:', featuresResponse);

      // Handle response structure
      let featuresData = [];
      if (Array.isArray(featuresResponse)) {
        featuresData = featuresResponse;
      } else if (featuresResponse?.data && Array.isArray(featuresResponse.data)) {
        featuresData = featuresResponse.data;
      } else if (featuresResponse?.data?.data && Array.isArray(featuresResponse.data.data)) {
        featuresData = featuresResponse.data.data;
      }

      // Step 2: Fetch existing tenant feature mappings (type='F')
      let existingMappings = [];
      try {
        const mappingResponse = await apiRequest(
          `/gateway/api/ticket/tenant-mapping/org/${orgId}/type/F`,
          { method: 'GET' }
        );

        //console.log('📦 Tenant feature mappings response:', mappingResponse);

        // Handle response structure
        if (Array.isArray(mappingResponse)) {
          existingMappings = mappingResponse;
        } else if (mappingResponse?.data && Array.isArray(mappingResponse.data)) {
          existingMappings = mappingResponse.data;
        } else if (mappingResponse?.data?.data && Array.isArray(mappingResponse.data.data)) {
          existingMappings = mappingResponse.data.data;
        }

        // Create a map of existing mappings: { featureScreenId: { id, isActive } }
        const mappingsMap = {};
        existingMappings.forEach(mapping => {
          const featureId = mapping.featureScreenId || mapping.feature_screen_id;
          if (featureId) {
            mappingsMap[featureId] = {
              id: mapping.id,
              isActive: mapping.isActive !== false,
              mappingId: mapping.id
            };
          }
        });

        // Step 3: Sync feature active status with mappings
        // Only show features that are active in the master_features table OR have a mapping
        const syncedFeatures = featuresData.map(feature => {
          const featureId = feature.id || feature.feature_id;
          const mapping = mappingsMap[featureId];
          
          return {
            ...feature,
            isActive: mapping ? mapping.isActive : false, // Default to false if no mapping exists
            mappingId: mapping?.mappingId || null // Store mapping ID for updates
          };
        });

        setFeatures(syncedFeatures);
      } catch (mappingError) {
        console.warn('Could not fetch existing feature mappings, using default status:', mappingError);
        // If mapping API fails, initialize all features as inactive
        const syncedFeatures = featuresData.map(feature => ({
          ...feature,
          isActive: false,
          mappingId: null
        }));
        setFeatures(syncedFeatures);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error(error.response?.data?.message || 'Failed to load master features');
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };


  const handleToggleActive = async (feature) => {
    if (!tenant?.tenantId) {
      toast.error('Tenant ID is required');
      return;
    }

    const orgId = tenant.tenantId;
    const featureId = feature.id || feature.feature_id;
    const newActiveState = !(feature.isActive !== undefined ? feature.isActive : feature.is_active);
    const existingMappingId = feature.mappingId || null;

    try {
      if (newActiveState) {
        // Enable: Create or update mapping
        const payload = {
          orgId: orgId,
          moduleId: null, // Features don't have module_id
          featureScreenId: featureId,
          type: 'F',
          isActive: true
        };

        if (existingMappingId) {
          // Update existing mapping
          await apiRequest(
            `/gateway/api/ticket/tenant-mapping/${existingMappingId}`,
            {
              method: 'PUT',
              body: { ...payload, isActive: true }
            }
          );
          
          // Soft patch: Update local state
          setFeatures(prev => prev.map(f => {
            const currentId = f.id || f.feature_id;
            if (currentId === featureId) {
              return {
                ...f,
                isActive: true,
                mappingId: existingMappingId
              };
            }
            return f;
          }));
        } else {
          // Create new mapping
          try {
            const response = await apiRequest(
              `/gateway/api/ticket/tenant-mapping/save`,
              {
                method: 'POST',
                body: payload
              }
            );
            
            // Extract mapping ID from response
            const mappingId = response?.data?.id || response?.id;
            if (mappingId) {
              // Soft patch: Update local state with mapping ID
              setFeatures(prev => prev.map(f => {
                const currentId = f.id || f.feature_id;
                if (currentId === featureId) {
                  return {
                    ...f,
                    isActive: true,
                    mappingId: mappingId
                  };
                }
                return f;
              }));
            } else {
              // Soft patch: Update local state immediately
              setFeatures(prev => prev.map(f => {
                const currentId = f.id || f.feature_id;
                if (currentId === featureId) {
                  return {
                    ...f,
                    isActive: true
                  };
                }
                return f;
              }));
            }
          } catch (saveError) {
            // If duplicate constraint error, try to update existing mapping
            if (saveError.response?.status === 400 || saveError.response?.data?.message?.includes('already exists')) {
              console.warn(`Mapping already exists for feature ${featureId}, updating...`);
              // Fetch existing mapping and update it
              const mappingsResponse = await apiRequest(
                `/gateway/api/ticket/tenant-mapping/org/${orgId}/type/F`,
                { method: 'GET' }
              );
              
              let mappings = [];
              if (Array.isArray(mappingsResponse)) {
                mappings = mappingsResponse;
              } else if (mappingsResponse?.data && Array.isArray(mappingsResponse.data)) {
                mappings = mappingsResponse.data;
              }
              
              const existingMapping = mappings.find(m => (m.featureScreenId || m.feature_screen_id) === featureId);
              if (existingMapping?.id) {
                await apiRequest(
                  `/gateway/api/ticket/tenant-mapping/${existingMapping.id}`,
                  {
                    method: 'PUT',
                    body: { ...payload, isActive: true }
                  }
                );
                
                // Soft patch: Update local state
                setFeatures(prev => prev.map(f => {
                  const currentId = f.id || f.feature_id;
                  if (currentId === featureId) {
                    return {
                      ...f,
                      isActive: true,
                      mappingId: existingMapping.id
                    };
                  }
                  return f;
                }));
              }
            } else {
              throw saveError;
            }
          }
        }
      } else {
        // Disable: Update mapping to set isActive = false
        if (existingMappingId) {
          // Update existing mapping
          await apiRequest(
            `/gateway/api/ticket/tenant-mapping/${existingMappingId}`,
            {
              method: 'PUT',
              body: {
                orgId: orgId,
                moduleId: null,
                featureScreenId: featureId,
                type: 'F',
                isActive: false
              }
            }
          );
          
          // Soft patch: Update local state
          setFeatures(prev => prev.map(f => {
            const currentId = f.id || f.feature_id;
            if (currentId === featureId) {
              return {
                ...f,
                isActive: false,
                mappingId: existingMappingId
              };
            }
            return f;
          }));
        } else {
          // No mapping exists for disabled feature - this is fine, just update local state
          // We don't create a mapping with isActive: false as it's not needed
          setFeatures(prev => prev.map(f => {
            const currentId = f.id || f.feature_id;
            if (currentId === featureId) {
              return {
                ...f,
                isActive: false
              };
            }
            return f;
          }));
        }
      }

      toast.success(`Feature ${newActiveState ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error(error.response?.data?.message || 'Failed to update feature status');
    }
  };


  const modalFooter = null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Master Features"
      size="xl"
      footer={modalFooter}
    >
      {/* Header Subtitle */}
      {tenant && (
        <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{tenant.tenantName || 'Tenant'}</span> (ID: {tenant.tenantId})
          </p>
        </div>
      )}


      {/* Features List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading features...</p>
        </div>
      ) : features.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <XCircle className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No features found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {features.map((feature) => {
            const featureId = feature.id || feature.feature_id;
            const featureName = feature.featureName || feature.feature_name || 'Unknown Feature';
            const isActive = feature.isActive !== undefined ? feature.isActive : (feature.is_active !== undefined ? feature.is_active : false);

            return (
              <div
                key={featureId}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                      {featureName}
                    </h4>
                    <Badge className={isActive 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                    }>
                      {isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isActive}
                      onCheckedChange={() => handleToggleActive(feature)}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {isActive ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default MasterFeaturesModal;
